import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  PresentationChartBarIcon,
  CircleStackIcon,
  BookOpenIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { StatsJSON, VersionInfo } from './types';
import { releasesApiService } from '../../services/releasesApiService';
import { apiReleaseToVersionsArray } from './releaseApiMappers';
import { compareVersions } from './helpers';

function toCompact(n: number | undefined): string {
  if (!n) return '—';
  if (n >= 1_000_000) {
    const v = n / 1_000_000;
    return `${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)}M+`;
  }
  if (n >= 1_000) return `${Math.floor(n / 1_000)}K+`;
  return n.toLocaleString();
}

const SECTIONS = [
  {
    id: 'statistics',
    title: 'Statistics',
    subtitle: 'Key numbers',
    to: '/docs?section=gdaStatistics',
    icon: PresentationChartBarIcon,
  },
  {
    id: 'data',
    title: 'Data',
    subtitle: 'Sources & metrics',
    to: '/docs?section=sources',
    icon: CircleStackIcon,
  },
  {
    id: 'cite',
    title: 'How to Cite',
    subtitle: 'Citation info',
    to: '/docs?section=cite_us',
    icon: BookOpenIcon,
  },
  {
    id: 'releases',
    title: 'Releases',
    subtitle: 'Version history',
    to: null,
    icon: RocketLaunchIcon,
  },
];

const MAGENTA = 'rgb(209, 0, 145)';

function SectionCircle({
  title,
  subtitle,
  to,
  icon: Icon,
}: {
  title: string;
  subtitle: string;
  to: string;
  icon: React.ElementType;
}) {
  return (
    <motion.div
      whileHover={{ scale: 1.06 }}
      transition={{ type: 'spring', stiffness: 380, damping: 22 }}
      className="flex flex-col items-center"
    >
      <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
        {/* Circle */}
        <div
          className="flex flex-col items-center justify-center rounded-full bg-white shadow-sm border border-gray-100 cursor-pointer"
          style={{ width: 168, height: 168 }}
        >
          <Icon style={{ width: '2rem', height: '2rem', color: MAGENTA, marginBottom: '0.5rem' }} />
          <span className="font-bold text-sm text-center leading-tight px-4">{title}</span>
          <span className="text-xs text-center mt-1 px-4" style={{ color: MAGENTA }}>
            {subtitle}
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

function SectionCard({
  title,
  subtitle,
  to,
  icon: Icon,
}: {
  title: string;
  subtitle: string;
  to: string;
  icon: React.ElementType;
}) {
  return (
    <motion.div whileHover={{ scale: 1.03 }} transition={{ type: 'spring', stiffness: 380, damping: 22 }}>
      <Link
        to={to}
        className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <Icon style={{ width: '1.75rem', height: '1.75rem', flexShrink: 0, color: MAGENTA }} />
        <div>
          <p className="font-bold text-sm leading-tight">{title}</p>
          <p className="text-xs mt-0.5" style={{ color: MAGENTA }}>
            {subtitle}
          </p>
        </div>
      </Link>
    </motion.div>
  );
}

function formatVersionWithDot(version: string): string {
  const clean = version.replace(/^v/, '');
  if (/^\d{3}$/.test(clean)) {
    return `${clean.slice(0, 2)}.${clean.slice(2)}`;
  }
  if (/^\d{4}$/.test(clean)) {
    return `${clean.slice(0, 2)}.${clean.slice(2, 3)}.${clean.slice(3)}`;
  }
  return clean;
}

export default function DocsHub() {
  const [stats, setStats] = useState<VersionInfo['statistics'] | null>(null);
  const [latestVersion, setLatestVersion] = useState<string>('');

  useEffect(() => {
    async function loadData() {
      try {
        const statsRes = await fetch('/stats.json');
        const statsData: StatsJSON = statsRes.ok ? await statsRes.json() : { current: '' };

        // Get all versions from both stats.json and API
        const statsVersions = Object.keys(statsData).filter(k => k !== 'current');

        // Fetch releases from API (S3)
        const apiReleasesList = await releasesApiService.getReleasesList();
        const apiVersions = apiReleasesList.map(r => r.version);

        // Combine all versions
        const allVersions = [...new Set([...statsVersions, ...apiVersions])];

        // Sort to get the latest
        if (allVersions.length > 0) {
          const sorted = allVersions.sort((a, b) => compareVersions(b, a));
          const latest = sorted[0];
          setLatestVersion(latest);

          const latestFromStats = statsData[latest];
          if (latestFromStats && typeof latestFromStats !== 'string') {
            setStats((latestFromStats as VersionInfo).statistics);
          } else {
            const latestRelease = await releasesApiService.getRelease(latest);
            const mapped = apiReleaseToVersionsArray(latestRelease);
            setStats(mapped.statistics);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    }

    loadData();
  }, []);

  const resolvedSections = SECTIONS.map(s => ({
    ...s,
    to: s.to ?? (latestVersion ? `/docs?version=${formatVersionWithDot(latestVersion)}` : '/docs'),
  }));

  const KEY_STATS = stats
    ? [
        { value: toCompact(stats.gda_summary), label: 'Gene-Disease\nAssociations' },
        { value: toCompact(stats.vda_summary), label: 'Variant-Disease\nAssociations' },
        { value: toCompact(stats.publication), label: 'Publications' },
        { value: toCompact(stats.dda), label: 'Disease-Disease\nAssociations' },
      ]
    : null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center py-16 px-4">
      <div className="text-center mb-12">
        <p className="text-xs font-semibold tracking-widest uppercase mb-3" style={{ opacity: 0.4 }}>
          DISGENET
        </p>
        <h1 className="font-extrabold tracking-tight" style={{ fontSize: '3.5rem', lineHeight: 1 }}>
          Documentation
        </h1>
        <p className="mt-4 text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
          Explore the knowledge base behind genotype–phenotype associations
        </p>
      </div>

      <div className="hidden md:flex items-end justify-center gap-8 mb-14">
        {resolvedSections.map(s => (
          <SectionCircle key={s.id} title={s.title} subtitle={s.subtitle} to={s.to} icon={s.icon} />
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 w-full max-w-sm md:hidden mb-10">
        {resolvedSections.map(s => (
          <SectionCard key={s.id} title={s.title} subtitle={s.subtitle} to={s.to} icon={s.icon} />
        ))}
      </div>

      {KEY_STATS && (
        <div className="flex gap-10 md:gap-16 justify-center flex-wrap">
          {KEY_STATS.map(stat => (
            <div key={stat.label} className="flex flex-col items-center text-center">
              <span className="text-4xl font-extrabold tracking-tight" style={{ color: MAGENTA }}>
                {stat.value}
              </span>
              <span className="text-xs text-gray-500 mt-1 whitespace-pre-line leading-tight">
                {stat.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
